// Track memory allocation and validation (P1.1, SP-4) — mirrors ring.ts's `createRing` /
// `assertRingCompatible` pair, for the same reason: allocation and validation run once, off any
// real-time path, and belong away from a reader that runs per output sample.
//
// Quantization lives here too, and it is worth saying why a "platform" file holds an audio
// judgment call. It is mechanical rather than interpretive — round, clamp, scale by a fixed
// constant — with no dependency on anything in /audio/dsp, so it costs nothing to keep beside the
// buffer it writes into. The part that *is* real DSP — Hermite interpolation on read — genuinely
// cannot live here: `platform-is-a-leaf` forbids /platform importing from /audio, and that rule is
// not being bent for this. See /audio/dsp/track-reader.ts for the other half.

import {
  computeTrackMemoryLayout,
  TRACK_MEMORY_FORMAT,
  TRACK_MEMORY_HEADER,
  TRACK_MEMORY_HEADER_BYTES,
  TRACK_MEMORY_MAGIC,
  TRACK_MEMORY_VERSION,
  trackChannelByteOffset,
  type TrackMemoryLayout,
} from '../types/track-memory';

const HEADER_INT32_LENGTH = TRACK_MEMORY_HEADER_BYTES / 4;
const MAGIC_AT = TRACK_MEMORY_HEADER.MAGIC / 4;
const VERSION_AT = TRACK_MEMORY_HEADER.VERSION / 4;
const FORMAT_AT = TRACK_MEMORY_HEADER.FORMAT / 4;
const CHANNEL_COUNT_AT = TRACK_MEMORY_HEADER.CHANNEL_COUNT / 4;
const FRAME_COUNT_AT = TRACK_MEMORY_HEADER.FRAME_COUNT / 4;
const SAMPLE_RATE_AT = TRACK_MEMORY_HEADER.SAMPLE_RATE_HZ / 4;

/**
 * Int16's full range is asymmetric (-32768..32767); scaling the positive side by 32767 and
 * dividing back by 32768 on read is the conventional PCM16 encode/decode pair (the same one WAV
 * files use). It leaves -32768 reachable only by a sample below -1.0, which real decoded audio
 * essentially never produces — and if it did, the clamp below is the backstop.
 */
const I16_ENCODE_SCALE = 32_767;
const I16_DECODE_SCALE = 32_768;

/** Sample → int16, for writing. Exported so the reader (a different layer) decodes symmetrically. */
export function quantizeToI16(sample: number): number {
  const clamped = Math.min(1, Math.max(-1, sample));
  return Math.round(clamped * I16_ENCODE_SCALE);
}

/** int16 → sample, for reading. The inverse of `quantizeToI16`, kept beside it so the pair can
 * never drift apart — a decode scale that stopped matching its encode scale would be silent gain
 * error, not a crash. */
export function dequantizeFromI16(raw: number): number {
  return raw / I16_DECODE_SCALE;
}

/**
 * Allocates a track-memory SharedArrayBuffer, stamps its header, and copies planar channel data
 * in — quantizing per-sample if the format is Int16.
 *
 * Called once, on the main thread, after `decodeAudioData` and before the worklet that will read
 * this buffer even exists — never on a real-time path, so the per-sample loop below is allowed to
 * cost what it costs. `channels[i]` is expected already at `sampleRateHz` (true by construction:
 * `decodeAudioData` resamples to the AudioContext that calls it, which is the context this rate
 * comes from).
 */
export function createTrackMemory(
  layout: TrackMemoryLayout,
  channels: readonly Float32Array[],
  sampleRateHz: number,
): SharedArrayBuffer {
  if (channels.length !== layout.channelCount) {
    throw new RangeError(
      `track memory: layout expects ${String(layout.channelCount)} channels, got ${String(channels.length)}`,
    );
  }

  const sab = new SharedArrayBuffer(layout.totalBytes);
  const header = new Uint32Array(sab, 0, HEADER_INT32_LENGTH);

  header[MAGIC_AT] = TRACK_MEMORY_MAGIC;
  header[VERSION_AT] = TRACK_MEMORY_VERSION;
  header[FORMAT_AT] = layout.format;
  header[CHANNEL_COUNT_AT] = layout.channelCount;
  header[FRAME_COUNT_AT] = layout.frameCount;
  header[SAMPLE_RATE_AT] = sampleRateHz;

  if (layout.format === TRACK_MEMORY_FORMAT.I16) {
    const view = new Int16Array(sab);
    for (let channel = 0; channel < channels.length; channel += 1) {
      const source = channels[channel];
      if (source === undefined) {
        continue;
      }
      const base = trackChannelByteOffset(layout, channel) / 2;
      for (let frame = 0; frame < layout.frameCount; frame += 1) {
        view[base + frame] = quantizeToI16(source[frame] ?? 0);
      }
      // Any pad sample past frameCount stays zero (a fresh SharedArrayBuffer is born zeroed) —
      // clampFrameIndex never reads past frameCount-1, so the pad exists only to keep the *next*
      // channel's byte offset aligned, and is genuinely never read.
    }
  } else {
    const view = new Float32Array(sab);
    for (let channel = 0; channel < channels.length; channel += 1) {
      const source = channels[channel];
      if (source === undefined) {
        continue;
      }
      const base = trackChannelByteOffset(layout, channel) / 4;
      view.set(source.subarray(0, layout.frameCount), base);
    }
  }

  return sab;
}

export interface TrackMemoryHeaderFields {
  readonly magic: number;
  readonly version: number;
  /** Plain `number`, unvalidated — this is a raw read. See TrackMemorySpec.format for why. */
  readonly format: number;
  readonly channelCount: number;
  readonly frameCount: number;
  readonly sampleRateHz: number;
}

/** Reads a buffer's own header back — what the worklet uses to rebuild the layout it was handed,
 * rather than trusting a layout object that crossed a thread boundary in a message. */
export function readTrackMemoryHeader(sab: SharedArrayBuffer): TrackMemoryHeaderFields {
  const header = new Uint32Array(sab, 0, HEADER_INT32_LENGTH);
  return {
    magic: header[MAGIC_AT] ?? 0,
    version: header[VERSION_AT] ?? 0,
    format: header[FORMAT_AT] ?? 0,
    channelCount: header[CHANNEL_COUNT_AT] ?? 0,
    frameCount: header[FRAME_COUNT_AT] ?? 0,
    sampleRateHz: header[SAMPLE_RATE_AT] ?? 0,
  };
}

/**
 * Refuses a buffer that is not a track-memory buffer this build understands.
 *
 * Same reasoning as `assertRingCompatible`: the buffer crosses from the main thread into
 * `processorOptions`, and while both sides ship in the same bundle today (unlike the rings, which
 * cross *bundle* boundaries), the check is nearly free and the failure it prevents — the worklet
 * reading music as a completely different sample count or format — is loud garbage rather than a
 * quiet one.
 */
export function assertTrackMemoryCompatible(
  sab: SharedArrayBuffer,
): TrackMemoryHeaderFields {
  const header = readTrackMemoryHeader(sab);

  if (header.magic !== TRACK_MEMORY_MAGIC) {
    throw new TypeError(
      'track memory: bad magic. Was this buffer created by createTrackMemory?',
    );
  }
  if (header.version !== TRACK_MEMORY_VERSION) {
    throw new TypeError(
      `track memory: buffer is version ${String(header.version)}, this build expects ${String(TRACK_MEMORY_VERSION)}.`,
    );
  }

  const layout = computeTrackMemoryLayout({
    channelCount: header.channelCount,
    frameCount: header.frameCount,
    format: header.format,
  });
  if (sab.byteLength !== layout.totalBytes) {
    throw new RangeError(
      `track memory: buffer is ${String(sab.byteLength)} bytes, header implies ${String(layout.totalBytes)}`,
    );
  }

  return header;
}
